/**
 * Powerbank detail drawer: uses shared DetailDrawer + DetailDrawerContent.
 * Shows powerbank info from API. Copyable fields, Block/Unblock, comment.
 */
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import * as api from '../../modules/api';
import { blockPowerBankService } from '../../modules/powerbanks/powerbanksServices';
import DetailDrawer from '../components/DetailDrawer';
import { Section, Field, CopyableField, FieldWithActions } from '../components/DetailDrawerContent';
import EntityCommentsSection, { normalizeEntityComments } from '../components/EntityCommentsSection';
import { formatApiDate } from '../utils/formatApiDate';
import { canBlockPowerbank, getCurrentRole } from '../utils/accessControl';

export default function PowerbankDetailDrawer({ powerbank, open, onClose, onRefresh }) {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const role = getCurrentRole();
  const canBlock = canBlockPowerbank(role);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockComment, setBlockComment] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [ejectLoading, setEjectLoading] = useState(false);

  useEffect(() => {
    if (!open || !powerbank) return;
    setComments(normalizeEntityComments(powerbank));
    setBlockComment('');
  }, [open, powerbank?.id, powerbank?.comment, powerbank?.comments, powerbank?.commentList]);

  const handleCopy = useCallback(
    (text) => {
      if (!text || text === '—') return;
      navigator.clipboard.writeText(String(text)).then(
        () => notifications.show(t('drawer.copied'), { severity: 'success', autoHideDuration: 2000 }),
        () => notifications.show(t('drawer.copyFailed'), { severity: 'error' }),
      );
    },
    [notifications, t],
  );

  const handleBlockUnblock = useCallback(async () => {
    if (!powerbank?.id) return;
    const newBlocked = !powerbank.blocked;
    setBlockLoading(true);
    try {
      await blockPowerBankService(powerbank.id, newBlocked, blockComment);
      setBlockDialogOpen(false);
      onRefresh?.();
      notifications.show(newBlocked ? t('drawer.powerbank.powerbankBlocked') : t('drawer.powerbank.powerbankUnblocked'), {
        severity: 'success',
        autoHideDuration: 3000,
      });
      onClose();
    } catch (e) {
      notifications.show(e?.message || t('drawer.actionFailed'), { severity: 'error' });
    } finally {
      setBlockLoading(false);
    }
  }, [powerbank?.id, powerbank?.blocked, blockComment, onRefresh, onClose, notifications, t]);

  const handleAddComment = useCallback(async (text) => {
    if (!powerbank?.id) return;
    const res = await api.commentPowerbank(powerbank.id, text);
    const data = res?.data;
    if (data) {
      setComments(normalizeEntityComments(data));
    } else {
      setComments((prev) => [...prev, { at: new Date().toISOString(), by: null, text }]);
    }
    notifications.show(t('drawer.comments.added'), { severity: 'success', autoHideDuration: 2000 });
    onRefresh?.();
  }, [powerbank?.id, notifications, t, onRefresh]);

  const handleOpenInStations = useCallback(() => {
    const stationId = powerbank?.station?.stationId;
    const stationNumber = powerbank?.station?.stationNumber;
    const identifier = stationNumber ?? stationId ?? '';
    if (identifier) {
      try {
        sessionStorage.setItem('crossLinkStation', String(identifier));
      } catch (_) {}
      window.location.hash = '#/station';
    }
  }, [powerbank?.station?.stationId, powerbank?.station?.stationNumber]);

  const handleEjectPowerbank = useCallback(async () => {
    const stationId = powerbank?.station?.stationId;
    const slot = powerbank?.slot;
    if (stationId == null || slot == null) {
      notifications.show(t('drawer.powerbank.stationOrSlotMissing'), { severity: 'error' });
      return;
    }
    setEjectLoading(true);
    try {
      await api.TakePowerBank(stationId, slot);
      notifications.show(t('drawer.powerbank.powerbankEjected'), { severity: 'success', autoHideDuration: 3000 });
      onRefresh?.();
      onClose();
    } catch (e) {
      notifications.show(e?.message || t('drawer.powerbank.failedToEject'), { severity: 'error' });
    } finally {
      setEjectLoading(false);
    }
  }, [powerbank?.station?.stationId, powerbank?.slot, onRefresh, onClose, notifications, t]);

  const station = powerbank?.station || {};
  const statusLabel = powerbank?.status || '—';
  const conditionLabel = powerbank?.checkingStatus || '—';
  const batteryLevel =
    powerbank?.batteryLevelPercent != null &&
    powerbank.batteryLevelPercent >= 0 &&
    powerbank.batteryLevelPercent <= 100
      ? `${powerbank.batteryLevelPercent}%`
      : '—';

  const hasStationLink = !!(station.stationId || station.stationNumber);
  const openInStationsButton = hasStationLink ? (
    <Tooltip title={t('drawer.powerbank.openInStations')}>
      <IconButton size="small" aria-label={t('drawer.powerbank.openInStations')} onClick={handleOpenInStations}>
        <OpenInNewOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  const canEject = powerbank?.status === 'IN_STATION' && station.stationId != null && powerbank?.slot != null;

  return (
    <>
      <DetailDrawer title={t('drawer.powerbank.title')} open={open} onClose={onClose}>
        <Box sx={{ minWidth: 0 }}>
          {!powerbank ? (
            <Typography color="text.secondary">{t('drawer.powerbank.noSelection')}</Typography>
          ) : (
            <>
          <Section title={t('drawer.powerbank.details')}>
            <CopyableField
              label={t('drawer.powerbank.id')}
              value={powerbank.powerBankId}
              onCopy={handleCopy}
              copyTooltip={t('drawer.copy')}
            />
            <Field label={t('drawer.powerbank.registration')} value={formatApiDate(powerbank.created)} />
            <Field label={t('drawer.powerbank.batteryLevel')} value={batteryLevel} />
          </Section>

          <Section title={t('drawer.powerbank.station')}>
            <FieldWithActions
              label={t('drawer.powerbank.stationId')}
              value={station.stationId}
              onCopy={handleCopy}
              copyTooltip={t('drawer.copy')}
              actionButton={openInStationsButton}
            />
            <FieldWithActions
              label={t('drawer.powerbank.stationNumber')}
              value={station.stationNumber}
              onCopy={handleCopy}
              copyTooltip={t('drawer.copy')}
              actionButton={openInStationsButton}
            />
            <Field
              label={t('drawer.powerbank.stationAddress')}
              value={station.address}
            />
            <Field label={t('drawer.powerbank.slot')} value={powerbank.slot} />
          </Section>

          <Section title={t('drawer.powerbank.statusBlocked')}>
            <Box sx={{ py: 0.75 }}>
              <Typography variant="caption" color="text.secondary" display="block">{t('drawer.powerbank.status')}</Typography>
              <Chip label={statusLabel} size="small" sx={{ mt: 0.5 }} />
            </Box>
            <Box sx={{ py: 0.75 }}>
              <Typography variant="caption" color="text.secondary" display="block">{t('drawer.powerbank.condition')}</Typography>
              <Chip label={conditionLabel} size="small" sx={{ mt: 0.5 }} />
            </Box>
            <Box sx={{ py: 0.75 }}>
              <Typography variant="caption" color="text.secondary" display="block">{t('drawer.powerbank.blocked')}</Typography>
              <Chip
                label={powerbank.blocked ? t('drawer.powerbank.yes') : t('drawer.powerbank.no')}
                color={powerbank.blocked ? 'error' : 'default'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Section>

          {canEject && (
            <Button
              variant="outlined"
              startIcon={<ExitToAppOutlinedIcon />}
              onClick={handleEjectPowerbank}
              disabled={ejectLoading}
              fullWidth
              sx={{ mb: 2 }}
            >
              {ejectLoading ? t('drawer.powerbank.ejecting') : t('drawer.powerbank.ejectPowerbank')}
            </Button>
          )}

          {canBlock && (
          <Button
            variant="outlined"
            color={powerbank.blocked ? 'success' : 'error'}
            startIcon={powerbank.blocked ? <LockOpenIcon /> : <BlockIcon />}
            onClick={() => setBlockDialogOpen(true)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {powerbank.blocked ? t('drawer.powerbank.unblock') : t('drawer.powerbank.block')}
          </Button>
          )}

          <EntityCommentsSection
            comments={comments}
            onAdd={handleAddComment}
          />
            </>
          )}
        </Box>
      </DetailDrawer>

      <Dialog
        open={blockDialogOpen}
        onClose={() => !blockLoading && setBlockDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{powerbank?.blocked ? t('drawer.powerbank.unblockConfirmTitle') : t('drawer.powerbank.blockConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {powerbank?.blocked
              ? t('drawer.powerbank.unblockConfirmMessage', { id: powerbank?.powerBankId })
              : t('drawer.powerbank.blockConfirmMessage', { id: powerbank?.powerBankId })}
          </Typography>
          {!powerbank?.blocked && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('drawer.powerbank.reasonOptional')}
              value={blockComment}
              onChange={(e) => setBlockComment(e.target.value)}
              variant="outlined"
              size="small"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)} disabled={blockLoading}>
            {t('drawer.cancel')}
          </Button>
          <Button
            variant="contained"
            color={powerbank?.blocked ? 'success' : 'error'}
            onClick={handleBlockUnblock}
            disabled={blockLoading}
          >
            {blockLoading ? '…' : powerbank?.blocked ? t('drawer.powerbank.unblock') : t('drawer.powerbank.block')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
